<template>
  <UIModal v-model="isOpen" blured :close-button="!isProcessing" :close-on-click-outside="!isProcessing">
    <!-- Header -->
    <template #header>
      <h2 class="halloween-expedition-modal__title">{{ $t('HalloweenCancelExpeditionModalTitle') }}</h2>
    </template>

    <!-- Content -->
    <div class="halloween-expedition-modal__content">
      <div class="halloween-expedition-modal__pirates-img">
        <img src="/img/halloween/PiratesBack.png" alt="pirates-back" />
      </div>

      <div class="halloween-expedition-modal__item-cards-row">
        <HalloweenItemFrame v-for="item in props.foundedItems" :key="item.id" variant="item" :content-gap="0">
          <UIItemDisplay
            :size="120"
            :item="item"
            :count="item.quantity"
            variant="default"
            direction="vertical"
            show-name
            :tooltip="false"
          />
        </HalloweenItemFrame>
      </div>

      <div class="halloween-expedition-modal__text">
        <p>
          {{
            $t(cannotInterrupt ? 'HalloweenCancelExpeditionWaitingModalText1' : 'HalloweenCancelExpeditionModalText1')
          }}
        </p>
        <p class="return-time-line">
          {{
            $t(cannotInterrupt ? 'HalloweenCancelExpeditionWaitingModalText2' : 'HalloweenCancelExpeditionModalText2')
          }}:
          <span class="return-timer-wrapper">
            <UIHalloweenCountdownTimer :target="displayTimeTarget" />
          </span>
        </p>
        <p>
          {{
            $t(cannotInterrupt ? 'HalloweenCancelExpeditionWaitingModalText3' : 'HalloweenCancelExpeditionModalText3')
          }}
        </p>
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="halloween-expedition-modal__footer">
        <HalloweenGenericButton :disabled="isProcessing" @click="handleGoBack">{{
          $t('halloweenButtonBack')
        }}</HalloweenGenericButton>
        <HalloweenGenericButton :disabled="isProcessing || cannotInterrupt" @click="interruptAction">{{
          $t('coreContinue')
        }}</HalloweenGenericButton>
      </div>
    </template>
  </UIModal>
</template>

<script setup lang="ts">
import UIItemDisplay from '~/components/Halloween/ui/UIItemDisplay.vue';
import type { ItemWithQuantity } from '~/types/halloween/items';
import UIHalloweenCountdownTimer from '~/components/Halloween/ui/UIHalloweenCountdownTimer.vue';

const props = defineProps<{
  isInterruptProcessing?: boolean;
  timeOfReturn: string | number | Date;
  foundedItems: ItemWithQuantity[];
}>();

const halloween = useHalloweenStore();
const location = computed(() => halloween.runtimeSelectedLocation);
const expeditionState = computed(() =>
  halloween.$state.statefulUserExpeditions.find((exp) => exp.location === location.value)
);

const displayTimeTarget = computed(() => {
  if (!expeditionState.value) return new Date();
  if (cannotInterrupt.value) return new Date(expeditionState.value.startedAt.getTime() + 1000 * 60 * 35);

  return new Date(Date.now() + (Date.now() - expeditionState.value.startedAt.getTime()) / 2);
});

const isOpen = defineModel<boolean>({ default: false });

const isProcessing = computed(() => props.isInterruptProcessing);

const handleGoBack = () => {
  isOpen.value = false;
};

const THIRTY_MINUTES_MS = 30 * 60 * 1000;

const cannotInterrupt = computed(() => {
  if (!expeditionState.value) return true;
  const timeElapsedMs = Date.now() - expeditionState.value.startedAt.getTime();
  return timeElapsedMs < THIRTY_MINUTES_MS;
});

const emit = defineEmits(['interrupt']);

const interruptAction = () => {
  emit('interrupt');
};
</script>

<style lang="scss" scoped>
.halloween-expedition-modal {
  &__title {
    font-family: 'PT Serif', serif;
    font-size: 32px;
    font-weight: bold;
    line-height: 30px;
    color: #ffffff;
    text-align: center;
    padding-left: 24px;
    padding-right: 24px;
    padding-bottom: 8px;

    @media (min-width: 640px) {
      padding-left: 48px;
      padding-right: 48px;
    }
  }

  &__content {
    font-family: 'PT Serif', serif;
    font-size: 18px;
    font-weight: 400;
    line-height: 110%;
    text-align: center;
    color: #dedede;
    padding-left: 24px;
    padding-right: 24px;

    max-width: 600px;
    margin: 0 auto;

    @media (min-width: 640px) {
      padding-left: 48px;
      padding-right: 48px;
    }

    @media screen and (max-width: 640px) {
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
    }
  }

  &__text {
    margin-bottom: 20px;

    p {
      margin-bottom: 4px;
    }

    .return-time-line {
      display: inline-block;
      gap: 4px;
      margin-bottom: 4px;
    }
  }

  &__pirates-img {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 20px;

    img {
      max-width: 180px;
      width: 100%;
      height: auto;
      display: block;
    }
  }

  &__item-cards-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: center;
    margin-bottom: 20px;
  }

  &__footer {
    display: flex;
    justify-content: center;
    gap: 10px;
    flex-wrap: wrap;
  }
}

.return-timer-wrapper {
  display: inline-block;
}
</style>
